import { api } from "@/lib/api";
import type { InboxQueue } from "@/lib/inboxQueues";

export interface AgencyBatchRecent { id: string; name: string; document_count: number; uploaded_by_name: string }
export async function getRecentAgencyBatches(selected: string, signal?: AbortSignal): Promise<AgencyBatchRecent[]> {
  return (await api.get<AgencyBatchRecent[]>("/agency-import/batches/recent", { signal, params: selected ? { selected } : undefined })).data;
}

export interface AgencyFile {
  id: string;
  path: string;
  folder: string;
  type_name: string | null;
  worker_id: string | null;
  candidates: string[];
  match_status: "pronaden" | "prijedlog" | "nije_pronaden" | "duplikat" | "firma";
  status: "preview" | "processing" | "skipped" | "duplicate" | "imported" | "inbox" | "failed";
  reason: string | null;
  inbox_id: string | null;
  confirmation_label?: string | null;
  inbox_queue?: InboxQueue | null;
  missing_fields?: string[];
  ai_retry_allowed?: boolean;
}

export interface AgencyBatch {
  id: string;
  status: "preview" | "processing" | "finished";
  workers: { id: string; name: string }[];
  files: AgencyFile[];
  archive_duplicates: number;
}

export interface AgencyChoice { id: string; worker_id: string | null; skip: boolean }

export async function previewAgencyPackage(files: File[]): Promise<AgencyBatch> {
  const data = new FormData();
  files.forEach((file) => data.append("files", file));
  return (await api.post<AgencyBatch>("/agency-import/preview", data)).data;
}

export async function getAgencyBatch(id: string, signal?: AbortSignal): Promise<AgencyBatch> {
  return (await api.get<AgencyBatch>(`/agency-import/${encodeURIComponent(id)}`, { signal })).data;
}

export async function commitAgencyPackage(id: string, choices: AgencyChoice[]): Promise<AgencyBatch> {
  return (await api.post<AgencyBatch>(`/agency-import/${encodeURIComponent(id)}/commit`, { choices })).data;
}

export function importChoices(batch: AgencyBatch, selected: Record<string, string>, skipped: Record<string, boolean>): AgencyChoice[] {
  return batch.files.filter((file) => file.status === "preview").map((file) => ({ id: file.id, worker_id: selected[file.id] || file.worker_id, skip: !!skipped[file.id] }));
}

export function unresolvedFiles(batch: AgencyBatch, choices: AgencyChoice[]): AgencyFile[] {
  return batch.files.filter((file) => file.status === "preview" && file.match_status !== "firma" && choices.some((choice) => choice.id === file.id && !choice.skip && !choice.worker_id));
}
